'use client';

import { usePathname } from 'next/navigation';
import Head from 'next/head';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: any;
  noIndex?: boolean;
  locale?: string;
  alternateLocales?: { hreflang: string; href: string }[];
}

export function SEO({
  title,
  description,
  keywords = 'AI, Machine Learning, Research, OpenLabs, Artificial Intelligence, AI Solutions, AI Services, AI Development',
  canonical: canonicalPath,
  ogImage = '/images/og-image.jpg',
  ogType = 'website',
  structuredData,
  noIndex = false,
  locale = 'en_AU',
  alternateLocales = [],
}: SEOProps) {
  const pathname = usePathname();
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://openlabsresearch.com';
  const canonical = canonicalPath || pathname || '/';
  const pageUrl = `${baseUrl}${canonical.startsWith('/') ? '' : '/'}${canonical}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  
  return (
    <Head>
      <title>{title} | OpenLabs Research</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={`${title} | OpenLabs Research`} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="OpenLabs Research" />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${title} | OpenLabs Research`} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Canonical */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Alternate languages */}
      {alternateLocales.map((alt) => (
        <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}
      
      {/* No Index */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Structured Data */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
    </Head>
  );
};
